/* prettier-ignore */

import { LOCALE_ID, NgModule, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import * as Sentry from "@sentry/angular";
import { ServiceWorkerModule } from '@angular/service-worker';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from 'src/app/shared/shared.module';
import { ToastrModule } from 'ngx-toastr'
import { BlurListenerModule } from './directives/blur-stats/blur-stats.module';
import { GalleryModule } from '@ks89/angular-modal-gallery';
import { ScrollCardModule } from './directives/scroll-card/scroll-card.module';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { DefaultMatCalendarRangeStrategy, MatDatepickerModule, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { ReactiveFormsModule } from '@angular/forms';
import { provideMarkdown, MARKED_OPTIONS } from 'ngx-markdown';
import { ScrollService } from '../chatbot/service/scrollContainer.service';
import { CookieService } from 'ngx-cookie-service';
import { ChatBotStandaloneComponent } from './shared/chat-bot-standalone/chat-bot-standalone.component';
import { Renderer } from 'marked';

import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeZh from '@angular/common/locales/zh';
import localeJa from '@angular/common/locales/ja';
import localeRu from '@angular/common/locales/ru';
import localeNl from '@angular/common/locales/nl';
import localePt from '@angular/common/locales/pt';
import localeKo from '@angular/common/locales/ko';
import localeEu from '@angular/common/locales/eu';
import localeGl from '@angular/common/locales/gl';
import localeCa from '@angular/common/locales/ca';

import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/ca';
import 'dayjs/locale/zh';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/ru';
import 'dayjs/locale/pt';
import 'dayjs/locale/gl';
import 'dayjs/locale/eu';
import 'dayjs/locale/nl';

const locales = [localeEn, localeFr, localeEs, localeDe, localeIt, localeZh, localeJa, localeRu, localeNl, localePt, localeKo, localeEu, localeGl, localeCa];
locales.forEach(locale => registerLocaleData(locale));

import { eye, pinOutline, arrowBackOutline, calendarOutline, close, closeCircleOutline, addOutline, menuOutline, eyeOff, map, grid, mail, exitOutline, save, informationCircle } from 'ionicons/icons';
import { addIcons } from 'ionicons';
import { InstallAppComponent } from './shared/install-app/install-app.component';
import { FallbackTranslateLoader } from './fallback-translate.loader';
import { provideLottieOptions } from 'ngx-lottie';
import { PassportFabButtonComponent } from './components/passport/fab-button/fab-button.component';
import { IncidentsReporterButtonComponent,  IncidentsReporterSurveyComponent } from './shared/incidents-reporter/components';

addIcons({
  'eye': eye,
  'eye-off': eyeOff,
  'pin-outline': pinOutline,
  'arrow-back-outline': arrowBackOutline,
  'calendar-outline': calendarOutline,
  'close': close,
  'close-circle-outline': closeCircleOutline,
  'add-outline': addOutline,
  'menu-outline': menuOutline,
  'map': map,
  'grid': grid,
  'mail': mail,
  'exit-outline': exitOutline,
  'save': save,
  'info': informationCircle
});

export function createTranslateLoader(http: HttpClient) {
  const url = 'https://cms-iurban.com/langs/';
  return new TranslateHttpLoader(http, url, '.php');
}

// export function HttpLoaderFactory(http: HttpClient) {
//   return new TranslateHttpLoader(http);
// }

export function createFallbackTranslateLoader() {
  return new FallbackTranslateLoader();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule, 
    BrowserAnimationsModule,
    IonicModule.forRoot(),
    ToastrModule.forRoot({
      closeButton: true,
      progressBar: true,
      positionClass: 'toast-top-right',
      preventDuplicates: true, 
    }),
    // ServiceWorkerModule.register('ngsw-worker.js', {
    //   enabled: false,
    //   // Register the ServiceWorker as soon as the application is stable
    //   // or after 30 seconds (whichever comes first).
    //   registrationStrategy: 'registerWhenStable:30000'
    // }),
    AppRoutingModule, 
    HttpClientModule,
    GalleryModule,
    ScrollCardModule,
    SharedModule,
    MatCardModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    ChatBotStandaloneComponent,
    BlurListenerModule,
    InstallAppComponent,
    PassportFabButtonComponent,
    IncidentsReporterButtonComponent,
    IncidentsReporterSurveyComponent,
  ],
  providers: [
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createFallbackTranslateLoader,
        deps: [HttpClient],
      },
    }),
    { provide: LOCALE_ID, useValue: 'es'},
    provideNativeDateAdapter(),
    DefaultMatCalendarRangeStrategy,
    MatRangeDateSelectionModel,
    ScrollService,
    CookieService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: () => {
          const renderer = new Renderer();
          renderer.link = (href: string, title: string, text: string) => {
            const titleAttr = title ? ` title="${title}"` : '';
            return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
          };
          return {
            renderer,
            gfm: true,
            breaks: false,
            smartLists: true,
            smartypants: false,
          };
        },
      },
    }),
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
  ],
  bootstrap: [AppComponent],
})

export class AppModule {}
